'use strict';
import sxQuery from '../sxQuery/sxQuery';
import Helper from '../siteSearch/utils/Helper';
import SxQueryUtils from '../sxQuery/SxQueryUtils';
import DeviceHelper from '../global/DeviceHelper';
const getDeviceType = DeviceHelper.getDeviceType;
var InsightsRequestType;
(function (InsightsRequestType) {
    InsightsRequestType["Post"] = "POST";
    InsightsRequestType["Get"] = "GET";
    InsightsRequestType["Image"] = "IMAGE";
})(InsightsRequestType || (InsightsRequestType = {}));
const SELECTED_LINK_KEY = 'ss360SelectedLinks';
const SELECTED_IDENTIFIER_KEY = 'ss360SelectedIdentifiers';
const SELECTED_ARTICLE_NUMBER_KEY = 'ss360SelectedArticleNumbers';
const getStringValue = (val) => {
    if (val === undefined) {
        return '---';
    }
    return val.trim();
};
const encodeComponent = (component) => encodeURIComponent(component !== undefined && component.split ? component.split('/').join('___') : component);
const DAY_MS = 24 * 60 * 60 * 1000; // day in ms
const SESSION_DURATION_MS = 10 * 60 * 1000; // session duration after last interaction
const CHECKOUT_RETENTION_DAYS = 14; // we remove all checkout related entries after 14 days with no interaction
const getNumberValue = (val) => {
    if (val === undefined || Helper.isNaN(val)) {
        return -1;
    }
    return val;
};
const generateElementSelector = (el, checkParent = true, encodeUri = false) => {
    if (!el) {
        return undefined;
    }
    let className = '';
    try {
        className = el.className && el.className ? `.${el.className.split(' ').join('.')}` : '';
    }
    catch (ex) {
        className = (el.getAttribute ? el.getAttribute('class') : '') || '';
    }
    const id = el.getAttribute('id') ? `#${el.getAttribute('id')}` : '';
    const parentEl = el.parentNode;
    let selector = '';
    if (parentEl && checkParent) {
        // @ts-ignore
        selector = `${generateElementSelector(parentEl, false)} `;
    }
    selector += el.tagName.toLowerCase() + id + className;
    return encodeUri ? encodeURIComponent(selector) : selector;
};
const removeOutdatedInteractionObject = (fullKey, entry) => {
    if (entry === undefined || entry === null || entry.updated === undefined || entry.updated === null) {
        return;
    }
    // remove everything older than CHECKOUT_RETENTION_DAYS days
    const start = new Date().getTime() - CHECKOUT_RETENTION_DAYS * DAY_MS;
    if (entry.updated < start) {
        SxQueryUtils._storeObject(fullKey, null);
    }
};
const normalizeLink = (link) => {
    if (link === undefined || link === null || link === '') {
        return link;
    }
    const hasProtocol = link.indexOf('http://') !== -1 || link.indexOf('https://') !== -1;
    if (hasProtocol) {
        return link;
    }
    if (link.indexOf('//') === 0) {
        return `${window.location.protocol}${link}`;
    }
    if (link.indexOf('/') !== 0) {
        link = `/${link}`;
    }
    return `${window.location.protocol}//${window.location.host}${link}`;
};
const readZoovuCid = () => {
    return SxQueryUtils.readCookie('zoovu-cid');
};
const getOrCreateZoovuCid = (sessionId, persist = true) => {
    let zoovuCid = readZoovuCid();
    if (zoovuCid === null) {
        zoovuCid = `${sessionId}_${new Date().getTime()}`;
        if (persist) {
            SxQueryUtils.createCookie('zoovu-cid', zoovuCid, 365, true);
        }
    }
    return zoovuCid;
};
const randomUUID = () => SxQueryUtils.uuid();
class SS360Insights {
    constructor(siteId, allowCookies, trackingConfig, cookiePostfix, generateZoovuCid = false) {
        this.generateZoovuCid = false;
        this.zoovuCidAsSessionId = false;
        this.debugMethod = (_message) => { };
        this.siteId = siteId;
        this.allowCookies = allowCookies;
        this.searchQueryCache = { query: undefined, timestamp: undefined };
        this.suggestionQueryCache = { query: undefined, timestamp: undefined };
        this.cookiePostfix = cookiePostfix;
        this.hasCheckoutTracking = false;
        this.generateZoovuCid = generateZoovuCid;
        this.zoovuCidAsSessionId = generateZoovuCid && trackingConfig.zoovuCidAsSessionId;
        this.config = {
            baseUrl: 'https://insights.sitesearch360.com/insights',
            endpoints: {
                // eslint-disable-next-line max-len
                serpSelect: '/v2/serp/select/##siteId##/##sessionId##/##timestamp##/##query##/##resultCount##/##position##/##positionInContentGroup##/##link##/##searchResultType##/##referrer##',
                miniPDPOpen: '/mini-pdp/open/##siteId##/##sessionId##/##timestamp##/##query##/##resultCount##/##position##/##positionInContentGroup##/##link##/##searchResultType##/##referrer##',
                miniPDPAbandon: '/mini-pdp/abandon/##siteId##/##sessionId##/##timestamp##/##query##/##link##/##referrer##',
                searchBoxFocus: '/searchbox/focus',
                searchBoxBulkChange: '/searchbox/change/bulk',
                searchBoxAbandon: '/searchbox/abandon',
                // eslint-disable-next-line max-len
                searchSubmit: '/v2/search/submit/##siteId##/##sessionId##/##timestamp##/##query##/##searchBoxId##/##searchButtonId##/##referrer##',
                suggestShow: '/suggests/show',
                // eslint-disable-next-line max-len
                suggestSelect: '/v2/suggests/select/##siteId##/##sessionId##/##timestamp##/##query##/##resultCount##/##searchBoxId##/##position##/##link##/##referrer##',
                serpShow: '/serp/show',
                // eslint-disable-next-line max-len
                serpAbandon: '/v2/serp/abandon/##siteId##/##sessionId##/##timestamp##/##query##/##resultCount##/##searchResultType##/##referrer##',
                filterInteraction: '/filter/interaction',
                addToCart: '/checkout/cart',
                buy: '/checkout/buy',
                relatedContentLoaded: '/related-content/load/##siteId##/##sessionId##/##timestamp##/##duration##/##query##/##count##/##referrer##/##id##',
                relatedContentViewed: '/related-content/view/##siteId##/##sessionId##/##timestamp##/##query##/##id##',
                relatedContentClicked: '/related-content/click/##siteId##/##sessionId##/##timestamp##/##query##/##resultLink##/##position##/##id##',
                guidedQuestionLoaded: '/guided-question/load/##siteId##/##sessionId##/##timestamp##/##duration##/##query##/##referrer##/##id##/##viewId##',
                guidedQuestionViewed: '/guided-question/view/##siteId##/##sessionId##/##timestamp##/##query##/##viewId##',
                guidedQuestionClicked: '/guided-question/click/##siteId##/##sessionId##/##timestamp##/##query##/##viewId##/##position##',
                zoeRendered: '/zoe/render/##siteId##/##sessionId##/##timestamp##/##query##/##viewId##/##referrer##',
                zoeInteracted: '/zoe/interact/##siteId##/##sessionId##/##timestamp##/##query##/##viewId##/##referrer##',
                zoeReset: '/zoe/reset/##siteId##/##sessionId##/##timestamp##/##query##/##viewId##/##referrer##',
                zoeOpen: '/zoe/open/##siteId##/##sessionId##/##timestamp##/##query##/##viewId##/##referrer##',
                zoeClose: '/zoe/close/##siteId##/##sessionId##/##timestamp##/##query##/##viewId##/##referrer##',
                refinementInteracted: '/refinement/interact/##siteId##/##sessionId##/##timestamp##/##query##/##referrer##',
            }
        };
        if (trackingConfig.enhancedParams !== undefined) {
            this.additionalParameters = trackingConfig.enhancedParams;
        }
        else {
            this.additionalParameters = {};
        }
        if (trackingConfig.userGroup !== undefined) {
            this.additionalParameters.userGroup = trackingConfig.userGroup;
        }
        if (trackingConfig.queryTag !== undefined) {
            this.additionalParameters.queryTag = trackingConfig.queryTag;
        }
        if (trackingConfig.enhancedBaseUrl !== undefined) {
            this.config.baseUrl = trackingConfig.enhancedBaseUrl;
        }
        this.sessionId = this.getSessionId();
        this.searchBoxChanges = {};
        this.latestSearchBoxValues = {};
        // reset outdated entries
        const selectedLinks = this.loadSearchInteractionEntry(SELECTED_LINK_KEY);
        const selectedIdentifiers = this.loadSearchInteractionEntry(SELECTED_IDENTIFIER_KEY);
        const selectedArticleNumbers = this.loadSearchInteractionEntry(SELECTED_ARTICLE_NUMBER_KEY);
        removeOutdatedInteractionObject(this.getSearchInteractionEntryKey(SELECTED_LINK_KEY), selectedLinks);
        removeOutdatedInteractionObject(this.getSearchInteractionEntryKey(SELECTED_IDENTIFIER_KEY), selectedIdentifiers);
        removeOutdatedInteractionObject(this.getSearchInteractionEntryKey(SELECTED_ARTICLE_NUMBER_KEY), selectedArticleNumbers);
    }
    updateLastInteractionTime() {
        if (this.zoovuCidAsSessionId) {
            return;
        }
        SxQueryUtils.createCookie(`ssi--lastInteraction${this.cookiePostfix}`, new Date().getTime().toString(), (SESSION_DURATION_MS) / DAY_MS);
    }
    loadSessionId() {
        if (this.zoovuCidAsSessionId) {
            const zoovuCid = readZoovuCid();
            if (zoovuCid !== null) {
                return zoovuCid.split('_')[0];
            }
            return null;
        }
        return SxQueryUtils.readCookie(`ssi--sessionId${this.cookiePostfix}`);
    }
    loadLastInteraction() {
        const lastInteraction = SxQueryUtils.readCookie(`ssi--lastInteraction${this.cookiePostfix}`);
        return lastInteraction !== undefined && lastInteraction !== null ? parseInt(lastInteraction, 10) : undefined;
    }
    getSessionId() {
        if (!this.allowCookies) {
            return this.generateSessionId();
        }
        const sessionId = this.loadSessionId();
        if (sessionId === null) {
            return this.generateSessionId();
        }
        if (!this.zoovuCidAsSessionId) {
            const lastInteraction = this.loadLastInteraction();
            if (lastInteraction === undefined || (new Date().getTime() - lastInteraction) > SESSION_DURATION_MS) {
                return this.generateSessionId(sessionId);
            }
        }
        return sessionId;
    }
    generateSessionId(previousSessionId) {
        const sessionId = randomUUID();
        const baseUrl = this.config.baseUrl;
        let additionalParamString = '';
        // eslint-disable-next-line no-undef
        // @ts-ignore
        if (SS360_VERSION !== undefined) {
            let versionNumber;
            // eslint-disable-next-line no-undef
            // @ts-ignore
            const versionParts = SS360_VERSION.toString().split('.'); // major.minor.patch
            if (versionParts.length > 1) {
                versionNumber = `${versionParts[0]}.${versionParts[1]}`;
            }
            if (versionNumber !== undefined) {
                additionalParamString += `version=${encodeURIComponent(versionNumber)}`;
            }
        }
        if (this.generateZoovuCid) {
            const zoovuCid = getOrCreateZoovuCid(sessionId, this.allowCookies);
            additionalParamString = additionalParamString.length > 0 ? `${additionalParamString}&` : '';
            additionalParamString = `${additionalParamString}zoovuCid=${encodeURIComponent(zoovuCid)}`;
        }
        const requestData = {
            siteId: this.siteId,
            sessionId,
            timestamp: new Date().getTime(),
            isPersistent: this.allowCookies,
            referrer: encodeURIComponent(getStringValue(window.location.href))
        };
        const testCookie = SxQueryUtils.readCookie('zoovu-test');
        if (testCookie !== null) {
            requestData.zoovuTest = encodeURIComponent(testCookie);
        }
        const sessionLog = {
            type: InsightsRequestType.Post,
            url: `${baseUrl}/session${additionalParamString.length > 0 ? '?' : ''}${additionalParamString}`,
            data: requestData
        };
        if (previousSessionId !== undefined) {
            // @ts-ignore
            sessionLog.data.prevSessionId = previousSessionId;
        }
        this.sendRequest(sessionLog);
        this.debugMethod('Created a search session');
        if (this.allowCookies && !this.zoovuCidAsSessionId) {
            SxQueryUtils.createCookie(`ssi--sessionId${this.cookiePostfix}`, sessionId, 365);
            this.updateLastInteractionTime(); // generating session id counts as interaction
        }
        return sessionId;
    }
    // ZTL components (e.g. <zoovu-comparison>) read the unprefixed ssi--sessionId/ssi--lastInteraction cookies and start a new session if they are missing or stale,
    // which is always the case when the zoovu-cid is used as session id - expose the current session id under those cookies so ZTL tracks into the same session
    syncSessionCookiesForZtl() {
        if (!this.allowCookies || !this.zoovuCidAsSessionId) {
            return;
        }
        let sessionId = this.loadSessionId();
        if (sessionId === null) {
            sessionId = this.sessionId;
        }
        SxQueryUtils.createCookie('ssi--sessionId', sessionId, 365);
        SxQueryUtils.createCookie('ssi--lastInteraction', new Date().getTime().toString(), (SESSION_DURATION_MS) / DAY_MS);
    }
    setDebugMethod(debugMethod) {
        this.debugMethod = debugMethod;
    }
    buildAdditionalParamsString() {
        if (this.additionalParameters === undefined || this.additionalParameters === null || Object.keys(this.additionalParameters).length === 0)
            return '';
        return Object.keys(this.additionalParameters).reduce((acc, key) => `${acc}&${encodeURIComponent(key)}=${encodeURIComponent(this.additionalParameters[key])}`, '').substring(1);
    }
    sendRequest(request) {
        let additionalParameters = this.buildAdditionalParamsString();
        const deviceParam = `device=${encodeURIComponent(getDeviceType())}`;
        additionalParameters = additionalParameters !== undefined && additionalParameters.length > 0 ? `${additionalParameters}&${deviceParam}` : deviceParam;
        if (additionalParameters !== undefined && additionalParameters.length > 0) {
            let requestUrl = request.url;
            if (requestUrl.indexOf('?') === -1) {
                requestUrl = `${requestUrl}?`;
            }
            else if (requestUrl[requestUrl.length - 1] !== '&' && requestUrl[requestUrl.length - 1] !== '?') {
                requestUrl = `${requestUrl}&`;
            }
            request.url = `${requestUrl}${additionalParameters}`;
        }
        if (request.type === InsightsRequestType.Image) {
            const i = new Image(1, 1);
            i.src = request.url;
        }
        else if (request.type === InsightsRequestType.Get) {
            SxQueryUtils.get(request.url);
        }
        else if (request.type === InsightsRequestType.Post) {
            SxQueryUtils.post(request.url, request.data);
        }
    }
    createImageRequest(endpoint, sessionId, data) {
        Object.keys(data).forEach((key) => {
            endpoint = endpoint.replace(`##${key}##`, encodeComponent(data[key]));
        });
        endpoint = endpoint.replace('##siteId##', encodeComponent(this.siteId));
        endpoint = endpoint.replace('##sessionId##', encodeComponent(sessionId));
        endpoint = endpoint.replace('##timestamp##', encodeComponent(new Date().getTime().toString()));
        endpoint = endpoint.replace(/##.+?##/g, '');
        return {
            type: InsightsRequestType.Image,
            url: this.config.baseUrl + endpoint
        };
    }
    pretrack() {
        if (this.allowCookies) {
            this.updateLastInteractionTime();
        }
    }
    trackSerpClick(query, eventTarget, resultBlock, resultCount, position, positionInContentGroup, link, searchResultType, articleNumber, identifier, clickSource, contentGroup, originalContentGroup) {
        this.pretrack();
        query = this.getMappedQuery(query);
        const hasEventTarget = eventTarget !== undefined && eventTarget !== null;
        if (identifier === undefined || identifier === null) {
            // @ts-ignore
            identifier = hasEventTarget ? sxQuery(eventTarget).data('productIdentifier') : undefined;
        }
        let queryParam = '';
        if ((articleNumber === undefined || articleNumber === null) && hasEventTarget) {
            // @ts-ignore
            articleNumber = sxQuery(eventTarget).data('ss360ArticleNumber') || sxQuery(eventTarget).data('productArticleNumber');
        }
        const queryParams = {};
        if (identifier) {
            queryParams.identifier = identifier;
        }
        if (articleNumber) {
            queryParams.articleNumber = articleNumber;
        }
        if (clickSource) {
            queryParams.clickSource = clickSource;
        }
        if (contentGroup) {
            queryParams.contentGroup = contentGroup;
        }
        if (originalContentGroup) {
            queryParams.originalContentGroup = originalContentGroup;
        }
        if (Object.keys(queryParams).length > 0) {
            queryParam = `?${Object.keys(queryParams).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`).join('&')}`;
        }
        link = normalizeLink(link);
        this.storeSearchInteraction(link, identifier, articleNumber);
        const request = this.createImageRequest(`${this.config.endpoints.serpSelect}${queryParam}`, this.sessionId, {
            query: getStringValue(query),
            resultCount: getNumberValue(resultCount),
            position: getNumberValue(position),
            positionInContentGroup: getNumberValue(positionInContentGroup),
            link: getStringValue(link),
            searchResultType: getStringValue(searchResultType),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked search result click');
    }
    trackMiniPDPOpen(query, eventTarget, resultCount, position, positionInContentGroup, link, searchResultType, articleNumber, identifier, contentGroup, originalContentGroup) {
        this.pretrack();
        const hasEventTarget = eventTarget !== undefined && eventTarget !== null;
        if (identifier === undefined || identifier === null) {
            // @ts-ignore
            identifier = hasEventTarget ? sxQuery(eventTarget).data('productIdentifier') : undefined;
        }
        let queryParam = '';
        if ((articleNumber === undefined || articleNumber === null) && hasEventTarget) {
            // @ts-ignore
            articleNumber = sxQuery(eventTarget).data('ss360ArticleNumber') || sxQuery(eventTarget).data('productArticleNumber');
        }
        const queryParams = {};
        if (identifier) {
            queryParams.identifier = identifier;
        }
        if (articleNumber) {
            queryParams.articleNumber = articleNumber;
        }
        if (contentGroup) {
            queryParams.contentGroup = contentGroup;
        }
        if (originalContentGroup) {
            queryParams.originalContentGroup = originalContentGroup;
        }
        if (Object.keys(queryParams).length > 0) {
            queryParam = `?${Object.keys(queryParams).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`).join('&')}`;
        }
        link = normalizeLink(link);
        this.storeSearchInteraction(link, identifier, articleNumber);
        const request = this.createImageRequest(`${this.config.endpoints.miniPDPOpen}${queryParam}`, this.sessionId, {
            query: getStringValue(query),
            resultCount: getNumberValue(resultCount),
            position: getNumberValue(position),
            positionInContentGroup: getNumberValue(positionInContentGroup),
            link: getStringValue(link),
            searchResultType: getStringValue(searchResultType),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked mini PDP open');
    }
    trackMiniPDPAbandon(query, link, articleNumber, identifier, contentGroup, originalContentGroup) {
        this.pretrack();
        let queryParam = '';
        const queryParams = {};
        if (identifier) {
            queryParams.identifier = identifier;
        }
        if (articleNumber) {
            queryParams.articleNumber = articleNumber;
        }
        if (contentGroup) {
            queryParams.contentGroup = contentGroup;
        }
        if (originalContentGroup) {
            queryParams.originalContentGroup = originalContentGroup;
        }
        if (Object.keys(queryParams).length > 0) {
            queryParam = `?${Object.keys(queryParams).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`).join('&')}`;
        }
        link = normalizeLink(link);
        this.storeSearchInteraction(link, identifier, articleNumber);
        const request = this.createImageRequest(`${this.config.endpoints.miniPDPAbandon}${queryParam}`, this.sessionId, {
            query: getStringValue(query),
            link: getStringValue(link),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked mini PDP abandon');
    }
    trackSearchBoxFocus(searchBox) {
        this.pretrack();
        const sessionId = this.sessionId;
        const data = {
            siteId: this.siteId,
            sessionId,
            timestamp: new Date().getTime(),
            searchBoxId: generateElementSelector(searchBox, true, true),
            referrer: getStringValue(window.location.href),
            query: getStringValue(searchBox.value)
        };
        this.sendRequest({
            url: this.config.baseUrl + this.config.endpoints.searchBoxFocus,
            type: InsightsRequestType.Post,
            data
        });
        this.debugMethod('Tracked search box focus');
    }
    collectSearchBoxChange(searchBox) {
        this.pretrack();
    }
    trackSubmitSearch(query, searchBox, searchButton, searchBoxId) {
        if (!query) {
            return;
        }
        query = this.getMappedQuery(query);
        this.pretrack();
        this.searchQueryCache.query = query;
        this.searchQueryCache.timestamp = new Date().getTime();
        const request = this.createImageRequest(this.config.endpoints.searchSubmit, this.sessionId, {
            query: getStringValue(query),
            searchBoxId: searchBoxId !== undefined ? getStringValue(searchBoxId) : getStringValue(generateElementSelector(searchBox, true)),
            searchButtonId: getStringValue(generateElementSelector(searchButton, true)),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked submit search');
    }
    trackSearchBoxAbandon(query, resultCount, searchBox) {
        if (query === undefined) {
            query = '';
        }
        query = this.getMappedQuery(query);
        this.pretrack();
        const sessionId = this.sessionId;
        const data = {
            siteId: this.siteId,
            sessionId,
            timestamp: new Date().getTime(),
            query,
            resultCount: getNumberValue(resultCount),
            searchBoxId: generateElementSelector(searchBox, true, true),
            referrer: getStringValue(window.location.href)
        };
        this.sendRequest({
            url: this.config.baseUrl + this.config.endpoints.searchBoxAbandon,
            type: InsightsRequestType.Post,
            data
        });
        this.debugMethod('Tracked search box abandon');
    }
    trackShowSuggests(searchBox, suggestBlock, suggestItem, query, resultCount, queryDuration, isEmptyState = false) {
        this.pretrack();
        query = this.getMappedQuery(query);
        const sessionId = this.sessionId;
        const data = {
            siteId: this.siteId,
            sessionId,
            timestamp: new Date().getTime(),
            query,
            resultCount: getNumberValue(resultCount),
            searchBoxId: generateElementSelector(searchBox, true, true),
            referrer: getStringValue(window.location.href)
        };
        if (queryDuration !== undefined) {
            // @ts-ignore
            data.queryDuration = queryDuration;
        }
        let url = this.config.baseUrl + this.config.endpoints.suggestShow;
        if (isEmptyState) {
            url = `${url}${url.indexOf('?') === -1 ? '?' : '&'}isEmptyState=true`;
        }
        this.sendRequest({
            url,
            type: InsightsRequestType.Post,
            data
        });
        this.debugMethod('Tracked suggestions displayed');
    }
    trackSelectSuggest(searchBox, suggestBlock, suggestItem, query, resultCount, position, link, contentGroup, source, articleNumber, identifier, isEmptyState = false) {
        this.pretrack();
        query = this.getMappedQuery(query);
        let url = this.config.endpoints.suggestSelect;
        const hasEventTarget = suggestItem !== null && suggestItem !== undefined;
        if (identifier === null || identifier === undefined) {
            // @ts-ignore
            identifier = hasEventTarget ? sxQuery(suggestItem).data('productIdentifier') : undefined;
        }
        if ((identifier === null || identifier === undefined) && hasEventTarget) {
            identifier = sxQuery(suggestItem).parents('.unibox__selectable', true).data('productIdentifier');
        }
        if ((articleNumber === undefined || articleNumber === null) && hasEventTarget) {
            // @ts-ignore
            articleNumber = sxQuery(suggestItem).data('ss360ArticleNumber') || sxQuery(suggestItem).data('productArticleNumber') || sxQuery(suggestItem).parents('.unibox__selectable', true).data('ss360ArticleNumber') || sxQuery(suggestItem).parents('.unibox__selectable', true).data('productArticleNumber');
        }
        const hasQueryParams = contentGroup !== undefined || source !== undefined || (identifier !== undefined && identifier !== null) || (articleNumber !== undefined && articleNumber !== null) || isEmptyState;
        let isFirst = true;
        if (hasQueryParams) {
            url = `${url}?`;
        }
        if (contentGroup !== undefined) {
            url = `${url}contentGroup=${encodeURIComponent(contentGroup)}`;
            isFirst = false;
        }
        if (source !== undefined) {
            url = `${url}${isFirst ? '' : '&'}source=${encodeURIComponent(source)}`;
            isFirst = false;
        }
        if (identifier !== undefined && identifier !== null && identifier !== '') {
            url = `${url}${isFirst ? '' : '&'}identifier=${encodeURIComponent(identifier)}`;
            isFirst = false;
        }
        if (articleNumber !== undefined && articleNumber !== null && articleNumber !== '') {
            url = `${url}${isFirst ? '' : '&'}articleNumber=${encodeURIComponent(articleNumber)}`;
            isFirst = false;
        }
        if (isEmptyState) {
            url = `${url}${isFirst ? '' : '&'}isEmptyState=true`;
        }
        link = normalizeLink(link);
        this.storeSearchInteraction(link, identifier, articleNumber);
        const request = this.createImageRequest(url, this.sessionId, {
            query: getStringValue(query !== undefined && query.length > 0 ? query : undefined),
            searchBoxId: getStringValue(generateElementSelector(searchBox, true)),
            resultCount: getNumberValue(resultCount),
            position: getNumberValue(position),
            link: getStringValue(link),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked search suggestion click');
    }
    trackSerpShow(resultBlock, resultItem, query, resultCount, resultType, filterValues, resultsAvailable) {
        this.pretrack();
        query = this.getMappedQuery(query);
        const sessionId = this.sessionId;
        const queryDuration = this.searchQueryCache.query === query ? (new Date().getTime() - this.searchQueryCache.timestamp) : undefined;
        const data = {
            siteId: this.siteId,
            sessionId,
            timestamp: new Date().getTime(),
            query,
            resultCount: getNumberValue(resultCount),
            searchResultType: getStringValue(resultType),
            referrer: getStringValue(window.location.href),
            filters: getStringValue(filterValues)
        };
        if (queryDuration !== undefined) {
            // @ts-ignore
            data.queryDuration = queryDuration;
        }
        let url = this.config.baseUrl + this.config.endpoints.serpShow;
        if (resultsAvailable !== undefined) {
            url = `${url}${(url.indexOf('?') === -1 ? '?' : '&')}resultsAvailable=${resultsAvailable}`;
        }
        this.sendRequest({
            url,
            type: InsightsRequestType.Post,
            data
        });
        this.debugMethod('Tracked search result page displayed');
    }
    trackSerpLeave(resultBlock, resultItem, query, resultCount, resultType, resultsAvailable) {
        this.pretrack();
        query = this.getMappedQuery(query);
        let url = this.config.endpoints.serpAbandon;
        if (resultsAvailable !== undefined) {
            url = `${url}${(url.indexOf('?') === -1 ? '?' : '&')}resultsAvailable=${resultsAvailable}`;
        }
        const request = this.createImageRequest(url, this.sessionId, {
            query: getStringValue(query),
            resultCount: getNumberValue(resultCount),
            searchResultType: getStringValue(resultType),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked search result page abandoned');
    }
    trackFilterInteraction(query, filterData) {
        this.pretrack();
        query = this.getMappedQuery(query);
        const sessionId = this.sessionId;
        const data = {
            siteId: this.siteId,
            sessionId,
            timestamp: new Date().getTime(),
            query,
            referrer: getStringValue(window.location.href),
            filters: getStringValue(filterData)
        };
        this.sendRequest({
            url: this.config.baseUrl + this.config.endpoints.filterInteraction,
            type: InsightsRequestType.Post,
            data
        });
        this.debugMethod('Tracked filter interaction');
    }
    trackAddToCart(articleNumber, identifier, link, count, price, priceUnit, fromSearch = false, fromSuggest = false, fromRelations = false, query) {
        if (!fromSearch && !fromSuggest && !fromRelations && !this.comesFromSearch(link, identifier, articleNumber)) {
            this.debugMethod('Not tracking add to cart, because this product wasn\'t interacted with within this search session');
            return;
        }
        this.pretrack();
        const sessionId = this.sessionId;
        link = normalizeLink(link);
        let source = 'pdp';
        if (fromSearch) {
            source = 'serp';
        }
        else if (fromSuggest) {
            source = 'suggest';
        }
        else if (fromRelations) {
            source = 'relations';
        }
        const data = {
            siteId: this.siteId,
            sessionId,
            timestamp: new Date().getTime(),
            link,
            count,
            priceUnit,
            referrer: getStringValue(window.location.href),
            source
        };
        if (query !== undefined && query !== null) {
            data.query = query;
        }
        if (price !== undefined && price !== null) {
            data.price = price;
        }
        if (identifier !== undefined && identifier !== null && identifier !== '') {
            data.identifier = identifier;
        }
        if (articleNumber !== undefined && articleNumber !== null && articleNumber !== '') {
            data.articleNumber = articleNumber;
        }
        if (fromSearch || fromSuggest || fromRelations) {
            this.storeSearchInteraction(link, identifier, articleNumber);
        }
        this.sendRequest({
            url: this.config.baseUrl + this.config.endpoints.addToCart,
            type: InsightsRequestType.Post,
            data
        });
        this.debugMethod('Tracked add to cart');
    }
    trackBuy(products, trackAll) {
        products.forEach((p) => p.link = normalizeLink(p.link));
        products = trackAll ? products : products.filter((p) => this.comesFromSearch(p.link, p.identifier, p.articleNumber));
        if (products.length === 0) {
            return;
        }
        products.forEach((p) => {
            if (p.fromSearch === undefined) {
                p.fromSearch = trackAll ? this.comesFromSearch(p.link, p.identifier, p.articleNumber) : true;
            }
        });
        this.pretrack();
        const sessionId = this.sessionId;
        const data = {
            siteId: this.siteId,
            sessionId,
            timestamp: new Date().getTime(),
            referrer: getStringValue(window.location.href),
            products: JSON.stringify(products)
        };
        this.sendRequest({
            url: this.config.baseUrl + this.config.endpoints.buy,
            type: InsightsRequestType.Post,
            data
        });
        this.debugMethod('Tracked checkout');
    }
    trackRelatedContentLoaded(query, duration, count, id, relationId, tag, originalQuery, isSerp = false) {
        this.pretrack();
        query = this.getMappedQuery(query);
        const queryParams = [];
        if (relationId !== undefined && relationId !== null) {
            queryParams.push(`relationId=${encodeURIComponent(relationId)}`);
        }
        if (tag !== undefined && tag !== null) {
            queryParams.push(`tag=${encodeURIComponent(tag)}`);
        }
        if (originalQuery !== undefined && originalQuery !== null) {
            queryParams.push(`originalQuery=${encodeURIComponent(originalQuery)}`);
        }
        if (isSerp) {
            queryParams.push(`isSerp=true`);
        }
        const queryString = queryParams.length === 0 ? '' : `?${queryParams.join('&')}`;
        const request = this.createImageRequest(`${this.config.endpoints.relatedContentLoaded}${queryString}`, this.sessionId, {
            query: getStringValue(query),
            duration: getNumberValue(duration),
            count: getNumberValue(count),
            id: getStringValue(id),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked relations loaded');
    }
    trackRelatedContentViewed(query, id, relationId, tag) {
        this.pretrack();
        query = this.getMappedQuery(query);
        const queryParams = [];
        if (relationId !== undefined && relationId !== null) {
            queryParams.push(`relationId=${encodeURIComponent(relationId)}`);
        }
        if (tag !== undefined && tag !== null) {
            queryParams.push(`tag=${encodeURIComponent(tag)}`);
        }
        const queryString = queryParams.length === 0 ? '' : `?${queryParams.join('&')}`;
        const request = this.createImageRequest(`${this.config.endpoints.relatedContentViewed}${queryString}`, this.sessionId, {
            query: getStringValue(query),
            id: getStringValue(id),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked relations viewed');
    }
    trackRelatedContentClicked(query, id, resultLink, position, relationId, tag, identifier) {
        this.pretrack();
        query = this.getMappedQuery(query);
        const queryParams = [];
        if (relationId !== undefined && relationId !== null) {
            queryParams.push(`relationId=${encodeURIComponent(relationId)}`);
        }
        if (tag !== undefined && tag !== null) {
            queryParams.push(`tag=${encodeURIComponent(tag)}`);
        }
        if (identifier !== undefined && identifier !== null) {
            queryParams.push(`identifier=${encodeURIComponent(identifier)}`);
        }
        const queryString = queryParams.length === 0 ? '' : `?${queryParams.join('&')}`;
        const request = this.createImageRequest(`${this.config.endpoints.relatedContentClicked}${queryString}`, this.sessionId, {
            query: getStringValue(query),
            id: getStringValue(id),
            referrer: getStringValue(window.location.href),
            resultLink: getStringValue(resultLink),
            position: getNumberValue(position)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked relations result clicked');
    }
    trackGuidedQuestionLoaded(query, id, viewId, duration) {
        query = this.getMappedQuery(query);
        this.pretrack();
        const request = this.createImageRequest(`${this.config.endpoints.guidedQuestionLoaded}`, this.sessionId, {
            query: getStringValue(query),
            id: getNumberValue(id),
            viewId: getNumberValue(viewId),
            duration: getNumberValue(duration),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked guided questions loaded');
    }
    trackGuidedQuestionViewed(query, viewId) {
        query = this.getMappedQuery(query);
        this.pretrack();
        const request = this.createImageRequest(`${this.config.endpoints.guidedQuestionViewed}`, this.sessionId, {
            query: getStringValue(query),
            viewId: getNumberValue(viewId)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked guided questions viewed');
    }
    trackGuidedQuestionClicked(query, viewId, position, answerId) {
        query = this.getMappedQuery(query);
        this.pretrack();
        const queryParam = answerId !== undefined && answerId !== null ? `?answerId=${answerId}` : '';
        const request = this.createImageRequest(`${this.config.endpoints.guidedQuestionClicked}${queryParam}`, this.sessionId, {
            query: getStringValue(query),
            viewId: getNumberValue(viewId),
            position: getNumberValue(position)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked guided question selected');
    }
    trackZoeRendered(query, viewId) {
        this.pretrack();
        const request = this.createImageRequest(`${this.config.endpoints.zoeRendered}`, this.sessionId, {
            query: getStringValue(query),
            viewId: getStringValue(viewId),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked ZOE rendered');
    }
    trackZoeInteracted(query, viewId) {
        this.pretrack();
        const request = this.createImageRequest(`${this.config.endpoints.zoeInteracted}`, this.sessionId, {
            query: getStringValue(query),
            viewId: getStringValue(viewId),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked ZOE interacted');
    }
    trackZoeReset(query, viewId) {
        this.pretrack();
        const request = this.createImageRequest(`${this.config.endpoints.zoeReset}`, this.sessionId, {
            query: getStringValue(query),
            viewId: getStringValue(viewId),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked ZOE reset');
    }
    trackZoeOpen(query, viewId) {
        this.pretrack();
        const request = this.createImageRequest(`${this.config.endpoints.zoeOpen}`, this.sessionId, {
            query: getStringValue(query),
            viewId: getStringValue(viewId),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked ZOE open');
    }
    trackZoeClose(query, viewId) {
        this.pretrack();
        const request = this.createImageRequest(`${this.config.endpoints.zoeClose}`, this.sessionId, {
            query: getStringValue(query),
            viewId: getStringValue(viewId),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked ZOE close');
    }
    trackRefinementInteracted(query, refinementText, refinementAction) {
        this.pretrack();
        const queryParams = [];
        if (refinementText !== undefined && refinementText !== null) {
            queryParams.push(`refinementText=${encodeURIComponent(refinementText)}`);
        }
        if (refinementAction !== undefined && refinementAction !== null) {
            queryParams.push(`refinementAction=${encodeURIComponent(JSON.stringify(refinementAction))}`);
        }
        const queryString = queryParams.length === 0 ? '' : `?${queryParams.join('&')}`;
        const request = this.createImageRequest(`${this.config.endpoints.refinementInteracted}${queryString}`, this.sessionId, {
            query: getStringValue(query),
            referrer: getStringValue(window.location.href)
        });
        this.sendRequest(request);
        this.debugMethod('Tracked refinement interacted');
    }
    setBaseUrl(baseUrl) {
        this.config.baseUrl = baseUrl;
    }
    setAdditionalParams(opts) {
        this.additionalParameters = opts;
    }
    storeSearchInteraction(link, identifier, articleNumber) {
        if (!this.allowCookies || !this.hasCheckoutTracking) {
            return;
        }
        this.storeSearchInteractionEntry(SELECTED_LINK_KEY, link);
        this.storeSearchInteractionEntry(SELECTED_IDENTIFIER_KEY, identifier);
        this.storeSearchInteractionEntry(SELECTED_ARTICLE_NUMBER_KEY, articleNumber);
    }
    comesFromSearch(link, identifier, articleNumber) {
        return (link !== undefined && link !== null && link !== '' && this.loadSearchInteractionEntry(SELECTED_LINK_KEY).data.indexOf(link) !== -1) ||
            (identifier !== undefined && identifier !== null && identifier !== '' && this.loadSearchInteractionEntry(SELECTED_IDENTIFIER_KEY).data.indexOf(identifier) !== -1) ||
            (articleNumber !== undefined && articleNumber !== null && articleNumber !== '' && this.loadSearchInteractionEntry(SELECTED_ARTICLE_NUMBER_KEY).data.indexOf(articleNumber) !== -1);
    }
    storeSearchInteractionEntry(key, value) {
        if (value !== undefined && value !== null && value !== '') {
            // @ts-ignore
            const storedData = this.loadSearchInteractionEntry(key);
            if (storedData.data.indexOf(value) === -1) {
                storedData.data.push(value);
                storedData.updated = new Date().getTime();
                SxQueryUtils._storeObject(this.getSearchInteractionEntryKey(key), storedData, CHECKOUT_RETENTION_DAYS);
            }
        }
    }
    getSearchInteractionEntryKey(baseKey) {
        return `${baseKey}${this.cookiePostfix}`;
    }
    loadSearchInteractionEntry(key) {
        // @ts-ignore
        return SxQueryUtils._readObject(this.getSearchInteractionEntryKey(key), { data: [], updated: undefined });
    }
    setHasCheckoutTracking(val) {
        this.hasCheckoutTracking = val;
    }
    getMappedQuery(query) {
        if (query === undefined || query === null || this.trackedQuery === undefined || this.trackedQuery === null) {
            return query;
        }
        return this.trackedQuery;
    }
}
export default SS360Insights;
